/**
 * FormValidator handles client-side form validation in a user-friendly and internationalized way.
 *
 * This class disables the browser's native validation popup and instead displays custom
 * error messages beside the input fields. It also highlights invalid inputs with a red border.
 *
 * The validation message can be localized by setting a `data-required-msg` attribute on each input.
 * This allows integration with Django's `{% trans %}` system for dynamic language switching (FR/EN).
 *
 * Usage:
 *
 * ```js
 * document.addEventListener('DOMContentLoaded', () => {
 *   new FormValidator(); // Applies to the first <form> on the page
 * });
 * ```
 */
class FormValidator {
  /**
   * Initializes validation for the specified form.
   * @param {string} formSelector - A CSS selector to target a specific form. Defaults to 'form'.
   */
  constructor(formSelector = 'form') {
    this.form = document.querySelector(formSelector);
    if (!this.form) return;

    this.inputs = this.form.querySelectorAll(
      'input[required], textarea[required], select[required]'
    );
    this.errorClass = 'input-error';
    this.errorStyle = 'text-red-500 text-xs mt-1';
    this.invalidBorderClass = '!border-red-500';

    this.init();
  }

  /**
   * Initializes input and submit event listeners.
   */
  init() {
    this.bindInputEvents();
    this.bindSubmitEvent();
  }

  /**
   * Binds input event listeners to clear errors when the user starts typing.
   */
  bindInputEvents() {
    this.inputs.forEach((input) => {
      input.addEventListener('input', () => this.clearError(input));
    });
  }

  /**
   * Binds submit event to validate required fields and prevent form submission if invalid.
   */
  bindSubmitEvent() {
    this.form.addEventListener('submit', (e) => {
      let isValid = true;

      this.inputs.forEach((input) => {
        if (input?.value?.trim() === '') {
          this.showError(input);
          isValid = false;
        } else {
          this.clearError(input);
        }
      });

      if (!isValid) e.preventDefault();
    });
  }

  /**
   * Displays a validation error message below the input field.
   * Uses `data-required-msg` attribute for localized message, with a default fallback.
   * @param {HTMLElement} input - The input element to show an error for.
   */
  showError(input) {
    const parent = this.getInputParent(input);
    const existingError = parent.querySelector(`.${this.errorClass}`);
    const errorMsg = input.dataset.requiredMsg || 'Ce champ est obligatoire.';

    input.classList.add(this.invalidBorderClass);

    if (!existingError) {
      const errorElement = document.createElement('p');
      errorElement.className = `${this.errorStyle} ${this.errorClass}`;
      errorElement.textContent = errorMsg;
      parent.appendChild(errorElement);
    }
  }

  /**
   * Clears the error message and red border from the input field.
   * @param {HTMLElement} input - The input element to clear errors from.
   */
  clearError(input) {
    const parent = this.getInputParent(input);
    const existingError = parent.querySelector(`.${this.errorClass}`);

    input.classList.remove(this.invalidBorderClass);
    if (existingError) existingError.remove();
  }

  /**
   * Gets the parent container of the input where the error message should be inserted.
   * Designed to work with templates where input is inside `div.mt-2`, which is wrapped.
   * @param {HTMLElement} input - The input element to find the parent for.
   * @returns {HTMLElement} The wrapping parent element.
   */
  getInputParent(input) {
    const mt2Div = input.closest('div.mt-2');
    return mt2Div ? mt2Div.parentElement : input.parentElement;
  }
}

export default FormValidator;
